import {
  createSessionRoutingDecision,
  isOnlineSessionMode,
  normalizeChatProvider,
  normalizeFallbackProviders,
  normalizeFlowType,
  normalizeRecordingProvider,
  normalizeRtcProvider,
  normalizeSessionMode,
  normalizeSignalingProvider,
  normalizeTranscriptionProvider,
} from './sessionRoutingContract'
import {
  deriveLegacySessionRouting,
  getLegacyPlatformIdFromSessionRouting,
  LEGACY_SESSION_PLATFORM_IDS,
  normalizeLegacyPlatformId,
} from './legacySessionRouting'

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function looksLikeSessionRoutingDecision(value) {
  if (!isPlainObject(value)) {
    return false
  }

  return (
    'rtcProvider' in value ||
    'signalingProvider' in value ||
    'resolvedByPolicy' in value ||
    'capabilities' in value ||
    'featureFlags' in value ||
    'fallbackProviders' in value ||
    'providerPayloads' in value ||
    ('sessionMode' in value && 'flowType' in value)
  )
}

function normalizeResolvedByPolicy(resolvedByPolicy, fallbackValue) {
  if (typeof resolvedByPolicy === 'string' && resolvedByPolicy.trim()) {
    return resolvedByPolicy
  }

  return fallbackValue
}

function createSessionRoutingDecisionFromResolvedMetadata(source) {
  if (!isPlainObject(source)) {
    return null
  }

  const rtcProvider = source?.rtcProviderResolved ?? source?.rtcProvider
  const signalingProvider = source?.signalingProviderResolved ?? source?.signalingProvider
  const chatProvider = source?.chatProviderResolved ?? source?.chatProvider
  const recordingProvider = source?.recordingProviderResolved ?? source?.recordingProvider
  const transcriptionProvider =
    source?.transcriptionProviderResolved ?? source?.transcriptionProvider

  const hasResolvedMetadata =
    source?.sessionMode ||
    rtcProvider ||
    signalingProvider ||
    source?.resolvedByPolicy ||
    source?.capabilities

  if (!hasResolvedMetadata) {
    return null
  }

  return {
    flowType: source?.flowType,
    sessionMode: source?.sessionMode,
    rtcProvider,
    signalingProvider,
    chatProvider,
    recordingProvider,
    transcriptionProvider,
    resolvedByPolicy: source?.resolvedByPolicy,
    fallbackProviders: source?.fallbackProviders,
    capabilities: source?.capabilities,
    providerPayloads: source?.providerPayloads,
    featureFlags: source?.featureFlags,
  }
}

function getFirstDecisionCandidate(candidates) {
  return (
    candidates
      .map((candidate) => {
        if (looksLikeSessionRoutingDecision(candidate)) {
          return candidate
        }

        return createSessionRoutingDecisionFromResolvedMetadata(candidate)
      })
      .find(Boolean) || null
  )
}

export function readSessionRoutingDecision(source) {
  if (looksLikeSessionRoutingDecision(source)) {
    return source
  }

  if (!isPlainObject(source)) {
    return null
  }

  return getFirstDecisionCandidate([
    source?.sessionRoutingDecision,
    source?.sessionRouting,
    source?.sessionRouting?.decision,
    source?.bookedEvent?.sessionRoutingDecision,
    source?.auxiliaryData?.sessionRoutingDecision,
    source?.data?.sessionRoutingDecision,
    source?.data?.data?.sessionRoutingDecision,
    source,
  ])
}

function getFirstLegacyPlatformId(candidates) {
  return (
    candidates
      .map((candidate) => normalizeLegacyPlatformId(candidate, null))
      .find(Boolean) || null
  )
}

export function readLegacyPlatformId(source, fallbackMeetingSrc) {
  const resolvedLegacyPlatformId = getFirstLegacyPlatformId([
    source?.legacyPlatformId,
    source?.sessionRouting?.legacyPlatformId,
    source?.bookedEvent?.legacyPlatformId,
    source?.auxiliaryData?.legacyPlatformId,
    source?.data?.legacyPlatformId,
    source?.data?.data?.legacyPlatformId,
  ])

  if (resolvedLegacyPlatformId) {
    return resolvedLegacyPlatformId
  }

  const resolvedSessionRouting = readSessionRoutingDecision(source)
  const resolvedMeetingSrc =
    fallbackMeetingSrc ||
    source?.meetingSrc ||
    source?.bookedEvent?.meetingSrc ||
    source?.data?.meetingSrc ||
    source?.data?.data?.meetingSrc

  return getLegacyPlatformIdFromSessionRouting(resolvedSessionRouting, resolvedMeetingSrc)
}

export function resolveSessionRouting({
  flowType,
  sessionData = {},
  sessionRoutingDecision,
  capabilityOverrides = {},
} = {}) {
  const legacySessionRouting = deriveLegacySessionRouting({
    flowType,
    sessionData,
    capabilityOverrides,
  })

  const providedSessionRouting =
    readSessionRoutingDecision(sessionRoutingDecision) || readSessionRoutingDecision(sessionData)

  if (!providedSessionRouting) {
    return legacySessionRouting
  }

  const rtcProvider = normalizeRtcProvider(
    providedSessionRouting?.rtcProvider,
    legacySessionRouting.rtcProvider,
  )

  return createSessionRoutingDecision({
    ...legacySessionRouting,
    flowType: normalizeFlowType(providedSessionRouting?.flowType, legacySessionRouting.flowType),
    sessionMode: normalizeSessionMode(
      providedSessionRouting?.sessionMode,
      legacySessionRouting.sessionMode,
    ),
    rtcProvider,
    signalingProvider: normalizeSignalingProvider(
      providedSessionRouting?.signalingProvider,
      legacySessionRouting.signalingProvider,
    ),
    chatProvider: normalizeChatProvider(
      providedSessionRouting?.chatProvider,
      legacySessionRouting.chatProvider,
    ),
    recordingProvider:
      normalizeRecordingProvider(providedSessionRouting?.recordingProvider) ??
      legacySessionRouting.recordingProvider,
    transcriptionProvider: normalizeTranscriptionProvider(
      providedSessionRouting?.transcriptionProvider,
      legacySessionRouting.transcriptionProvider,
    ),
    resolvedByPolicy: normalizeResolvedByPolicy(
      providedSessionRouting?.resolvedByPolicy,
      legacySessionRouting.resolvedByPolicy,
    ),
    fallbackProviders: normalizeFallbackProviders(
      providedSessionRouting?.fallbackProviders,
      rtcProvider,
    ),
    capabilities: {
      ...legacySessionRouting.capabilities,
      ...(isPlainObject(providedSessionRouting?.capabilities)
        ? providedSessionRouting.capabilities
        : {}),
      ...(isPlainObject(capabilityOverrides) ? capabilityOverrides : {}),
    },
    providerPayloads: isPlainObject(providedSessionRouting?.providerPayloads)
      ? providedSessionRouting.providerPayloads
      : legacySessionRouting.providerPayloads,
    featureFlags: isPlainObject(providedSessionRouting?.featureFlags)
      ? providedSessionRouting.featureFlags
      : legacySessionRouting.featureFlags,
  })
}

export function resolveSessionRoutingContext({
  flowType,
  sessionData = {},
  sessionRoutingDecision,
  capabilityOverrides = {},
} = {}) {
  const resolvedSessionRouting = resolveSessionRouting({
    flowType,
    sessionData,
    sessionRoutingDecision,
    capabilityOverrides,
  })
  const legacyPlatformId = readLegacyPlatformId(
    {
      ...sessionData,
      sessionRoutingDecision,
    },
    sessionData?.meetingSrc,
  )
  const isOnlineSession = isOnlineSessionMode(resolvedSessionRouting.sessionMode)

  return {
    sessionRoutingDecision: resolvedSessionRouting,
    legacyPlatformId,
    sessionMode: resolvedSessionRouting.sessionMode,
    rtcProviderResolved: resolvedSessionRouting.rtcProvider,
    signalingProviderResolved: resolvedSessionRouting.signalingProvider,
    isOnlineSession,
    isVideoSession:
      isOnlineSession && legacyPlatformId === LEGACY_SESSION_PLATFORM_IDS.agora,
  }
}

export function decorateSessionWithRouting(sessionData, options = {}) {
  if (!isPlainObject(sessionData)) {
    return sessionData
  }

  const routingContext = resolveSessionRoutingContext({
    ...options,
    sessionData,
  })

  return {
    ...sessionData,
    ...routingContext,
  }
}

const loggedRoutingDiagnostics = new Set()

export function buildSessionRoutingDiagnostics({
  label,
  flowType,
  sessionData = {},
  sessionRoutingDecision,
  capabilityOverrides = {},
  context = {},
} = {}) {
  const routingContext = resolveSessionRoutingContext({
    flowType,
    sessionData,
    sessionRoutingDecision,
    capabilityOverrides,
  })

  return {
    label: label || 'session-routing',
    flowType: routingContext.sessionRoutingDecision.flowType,
    bookedEventId:
      context?.bookedEventId || sessionData?.id || sessionData?.bookedEventId || null,
    sessionSlug: context?.sessionSlug || null,
    sessionMode: routingContext.sessionMode,
    legacyPlatformId: routingContext.legacyPlatformId,
    rtcProviderResolved: routingContext.rtcProviderResolved,
    signalingProviderResolved: routingContext.signalingProviderResolved,
    resolvedByPolicy: routingContext.sessionRoutingDecision.resolvedByPolicy,
    fallbackProviders: routingContext.sessionRoutingDecision.fallbackProviders,
  }
}

export function logSessionRoutingDiagnostics(options = {}) {
  const diagnostics = buildSessionRoutingDiagnostics(options)
  const diagnosticKey = JSON.stringify(diagnostics)

  if (!loggedRoutingDiagnostics.has(diagnosticKey)) {
    loggedRoutingDiagnostics.add(diagnosticKey)
    console.info('[RTC routing]', diagnostics)
  }

  return diagnostics
}
